// Copyright (c) 2016 Deere & Company

import './claim-history-report.scss';
import {
    FormattedMessage,
    injectIntl,
    intlShape
} from 'react-intl';
import React, {
    Component,
    PropTypes
} from 'react';
import ClaimHistory from '@deere/cps-it.warranty-model-factory/lib/claim-history';
import ClaimHistoryReportCriteria from './claim-history-report-criteria';
import Criteria from '../state/criteria';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getClaimHistoryColumnDefinitions} from '../report-services/column-definitions';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleCreateClaimHistoryReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.createClaimHistoryReport();

}

function handleClaimTypeSelect(claimType) {

    this.actions.selectClaimHistoryClaimType(claimType, this.claimTypes);

}

function handleCreditMemoDateSelect(creditMemoDate) {

    this.actions.selectCreditMemoProcessDate(creditMemoDate);

}

function handleDealerSelect(dealer) {

    this.actions.selectClaimHistoryDealer(dealer, this.selectedAccounts);

}

function handleExportToCsv() {

    const fileName = 'claim-history';

    return exportToCSV(fileName, this.gridApi);

}

function handleFirstWorkOrderInputChange(workOrderOneInput) {

    const workOrderInput = workOrderOneInput.target.value;

    this.actions.updateClaimHistoryCriteria('workOrderOne', workOrderInput);

}

function handlePinCriteriaInput(pinInputEvent) {

    const pinInput = pinInputEvent.target.value;

    this.actions.updateClaimHistoryCriteria('pin', pinInput);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleSecondWorkOrderInputChange(workOrderTwoInput) {

    const workOrderInput = workOrderTwoInput.target.value;

    this.actions.updateClaimHistoryCriteria('workOrderTwo', workOrderInput);

}

function handleCriteriaReset() {

    this.actions.resetClaimHistoryReport();
    this.actions.updateShowNoResultsFound(false);

}

class ClaimHistoryReport extends Component {

    async componentWillMount() {

        this.props.actions.readClaimTypes();
        await this.props.actions.getReportLayouts('claim-history');
        this.props.actions.updateGridLayouts(this.props.layouts, getClaimHistoryColumnDefinitions());

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetClaimHistoryReport();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const canExportToExcel = this.props.report.length > 0;
        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='claim-history'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='claim-history-title'>
                    <FormattedMessage id='CLAIM_HISTORY'/>
                    <FavoriteIcon
                        id='CLAIM_HISTORY'
                        route={this.props.pathName}
                    />
                </h2>
                <ClaimHistoryReportCriteria
                    canExport={canExportToExcel}
                    canRunReport={this.props.isCriteriaValid}
                    claimTypes={this.props.claimTypes}
                    criteria={this.props.criteria}
                    handleClaimTypeSelect={this.props::handleClaimTypeSelect}
                    handleCreditMemoDateSelect={this.props::handleCreditMemoDateSelect}
                    handleCriteriaReset={this.props::handleCriteriaReset}
                    handleDealerSelect={this.props::handleDealerSelect}
                    handleFirstWorkOrderInput={this.props::handleFirstWorkOrderInputChange}
                    handlePinInputChange={this.props::handlePinCriteriaInput}
                    handleSecondWorkOrderInput={this.props::handleSecondWorkOrderInputChange}
                    onCreateClaimHistoryReport={this.props::handleCreateClaimHistoryReport}
                    onExportToCsv={this.props::handleExportToCsv}
                    selectedAccounts={this.props.selectedAccounts}
                />
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='claim-history'
                            />
                            <ReportGrid
                                columnDefs={getClaimHistoryColumnDefinitions()}
                                gridOptions={gridOptions}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={this.props.report}
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

ClaimHistoryReport.displayName = 'ClaimHistoryReport';
ClaimHistoryReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    claimTypes: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair).isRequired),
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    gridApi: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    isCriteriaValid: PropTypes.bool.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(ClaimHistory)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired
};

export default injectIntl(ClaimHistoryReport);



// WEBPACK FOOTER //
// ./src/reports/claim-history/components/claim-history-report.js